/**
 * @file Maintenance — the §6.3 machine, the ≤14-day reopened window, the channel
 * tags and the input schemas (T1.6).
 *
 * Four things live here, and they are one decision seen from four angles: *what
 * was asked for, how it arrived, and what may happen to it next?*
 *
 *   1. **The §6.3 states and the transition map, as data.** `Record<MaintenanceStatus,
 *      …>` is total, so a state added without saying where it goes fails to
 *      compile rather than silently becoming unreachable — the same shape
 *      `leases.ts` (§6.2) and `rent-charges.ts` (§6.1) use.
 *   2. **The ≤14-day reopened window as a PURE PREDICATE over instants**
 *      (`evaluateMaintenanceReopenWindow`). §6.3 draws `completed → reopened
 *      (≤14 days)`; the parenthesised window is a real precondition, not
 *      decoration. It is NOT an unqualified edge in the map and it is NOT a check
 *      buried in a route: it takes the completion instant and "now" and touches
 *      nothing, so its boundary is testable without a database.
 *   3. **The channel tags.** §5 calls `channel` "the activation instrument", and
 *      §6.3 says a landlord "may log on behalf of tenant with channel tag". The
 *      three on-behalf channels are separated from `portal` here (see
 *      `MAINTENANCE_ON_BEHALF_CHANNELS`) because that separation is the only
 *      thing that distinguishes a request a tenant phoned in from one they filed
 *      in the portal.
 *   4. **The input schemas and the DTOs** the API and the SPA share.
 *
 * The kernel discipline applies as everywhere in `packages/shared`: no drizzle,
 * no app code. The SPA imports these TYPES and the label maps; the API is the
 * authority on what is valid.
 */
import { z } from "zod";
import type { LeaseStatus } from "./leases.js";

/**
 * The §6.3 maintenance states, in the order PLAN.md §6.3 draws them.
 *
 * Mirrored by the `maintenance_status` enum in
 * `packages/db/migrations/0009_maintenance.sql`; a state added in one place and
 * not the other is refused by the enum at write time.
 */
export const MAINTENANCE_STATUSES = [
  "submitted",
  "acknowledged",
  "in_progress",
  "completed",
  "cancelled",
  "reopened",
] as const;

export type MaintenanceStatus = (typeof MAINTENANCE_STATUSES)[number];

/** Mutable-tuple form, for `z.enum` (mirrors LEASE_STATUS_VALUES). */
export const MAINTENANCE_STATUS_VALUES = MAINTENANCE_STATUSES.map(
  (status) => status,
) as [MaintenanceStatus, ...MaintenanceStatus[]];

/**
 * Human labels for the same six states.
 *
 * Here rather than in the SPA because the list, the detail timeline and any
 * future notification will render them, and `in_progress` appearing raw in a
 * badge is how a status list starts disagreeing with itself.
 */
export const MAINTENANCE_STATUS_LABELS: Readonly<Record<MaintenanceStatus, string>> = {
  submitted: "Submitted",
  acknowledged: "Acknowledged",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
  reopened: "Reopened",
};

/**
 * §5's channel enum, verbatim: the activation instrument.
 *
 * `portal` is the tenant portal's instrument (T1.7). `text`, `call` and
 * `in_person` are the ones a LANDLORD records when a tenant reports something
 * off-platform — §6.3's "log on behalf of tenant".
 */
export const MAINTENANCE_CHANNELS = ["portal", "text", "call", "in_person"] as const;

export type MaintenanceChannel = (typeof MAINTENANCE_CHANNELS)[number];

/** Mutable-tuple form, for `z.enum`. */
export const MAINTENANCE_CHANNEL_VALUES = MAINTENANCE_CHANNELS.map(
  (channel) => channel,
) as [MaintenanceChannel, ...MaintenanceChannel[]];

/** Human labels for the four instruments. */
export const MAINTENANCE_CHANNEL_LABELS: Readonly<Record<MaintenanceChannel, string>> = {
  portal: "Portal",
  text: "Text",
  call: "Call",
  in_person: "In person",
};

/**
 * The channels the landlord-side create route accepts — §6.3's on-behalf set.
 *
 * **`portal` is deliberately NOT here.** On this surface a landlord is recording
 * what a tenant told them, so `portal` cannot be true of any request created
 * through it: the portal is the instrument the tenant uses (T1.7), and a
 * landlord-side caller asserting `portal` would record an origin that did not
 * happen. Widening this list is a plan change, not a convenience — the moment a
 * portal route exists it will write `portal` itself.
 */
export const MAINTENANCE_ON_BEHALF_CHANNELS = ["text", "call", "in_person"] as const;

export type MaintenanceOnBehalfChannel = (typeof MAINTENANCE_ON_BEHALF_CHANNELS)[number];

/** Mutable-tuple form, for `z.enum`. */
export const MAINTENANCE_ON_BEHALF_CHANNEL_VALUES = MAINTENANCE_ON_BEHALF_CHANNELS.map(
  (channel) => channel,
) as [MaintenanceOnBehalfChannel, ...MaintenanceOnBehalfChannel[]];

/**
 * Did this request arrive through the landlord rather than through the portal?
 *
 * One definition, exported, so the API and the screen cannot disagree about which
 * channels count as on-behalf logging.
 */
export function isOnBehalfChannel(channel: MaintenanceChannel): boolean {
  return (MAINTENANCE_ON_BEHALF_CHANNELS as readonly MaintenanceChannel[]).includes(channel);
}

/** What one timeline row is (§5's "kind or equivalent"). Mirrors 0009. */
export const MAINTENANCE_EVENT_KINDS = ["status_transition", "note"] as const;

export type MaintenanceEventKind = (typeof MAINTENANCE_EVENT_KINDS)[number];

/** Mutable-tuple form, for `z.enum`. */
export const MAINTENANCE_EVENT_KIND_VALUES = MAINTENANCE_EVENT_KINDS.map(
  (kind) => kind,
) as [MaintenanceEventKind, ...MaintenanceEventKind[]];

/** Human labels for the two timeline kinds. */
export const MAINTENANCE_EVENT_KIND_LABELS: Readonly<
  Record<MaintenanceEventKind, string>
> = {
  status_transition: "Status change",
  note: "Note",
};

/**
 * The §6.3 transition map, as data.
 *
 * Every state has an entry — `Record<MaintenanceStatus, …>` is total, so adding a
 * state without saying where it can go fails to compile rather than silently
 * becoming unreachable. The edges are exactly the ones §6.3 draws:
 *
 *     submitted → acknowledged → in_progress → completed
 *     → cancelled
 *     completed → reopened (≤14 days)
 *
 * **Where `cancelled` is reachable from, and why it is only `submitted`.**
 * §6.3 writes `→ cancelled` with NO source state. Compare §6.1, which writes
 * "any-unpaid `→ written_off`" — the plan quantifies an arrow when it means more
 * than one source, so the missing quantifier here is meaningful: the source is
 * undrawn. The narrowest reading that keeps the state reachable at all (a state
 * with no inbound edge is dead, and the enum would carry a value nothing can ever
 * reach) is therefore exactly ONE edge, and the only defensible single source is
 * `submitted`: it is the state in which no work has begun, so cancelling there
 * discards a genuinely duplicate or mistaken request without throwing away an
 * acknowledgement or work in flight. `→ cancelled` from `acknowledged` or
 * `in_progress` is the tempting broader reading — a tenant who says "never mind"
 * after triage — and it is deliberately NOT drawn: a request someone has started
 * work on is closed by completing it, and adding that edge is inventing a state
 * machine the plan does not specify.
 *
 * **`completed` has one edge and it is the guarded one.** §6.3 draws
 * `completed → reopened`, so the edge is here; the "(≤14 days)" is a precondition
 * enforced by `evaluateMaintenanceReopenWindow` below, never by this table alone.
 * `cancelled` and `reopened` are terminal: §6.3 draws nothing out of either.
 */
export const MAINTENANCE_TRANSITIONS: Readonly<
  Record<MaintenanceStatus, readonly MaintenanceStatus[]>
> = {
  // §6.3's "landlord may log on behalf of tenant": a logged request starts here
  // and is acknowledged when someone has seen it.
  submitted: ["acknowledged", "cancelled"],
  // Triage happened; the next move is that work starts.
  acknowledged: ["in_progress"],
  in_progress: ["completed"],
  // Drawn by §6.3 — and gated by the ≤14-day window below, so this edge is legal
  // only while the predicate says so.
  completed: ["reopened"],
  // Terminal: the request was called off before work began. Nothing follows it.
  cancelled: [],
  // Terminal: a reopened request is a completed one whose repair did not hold.
  // §6.3 draws no further move, and a second reopen is not a state move — it is
  // the same judgement being made again, which the window below would refuse
  // anyway.
  reopened: [],
};

/** May a request move from `from` to `to`? The §6.3 map, as one question. */
export function canTransitionMaintenance(
  from: MaintenanceStatus,
  to: MaintenanceStatus,
): boolean {
  return MAINTENANCE_TRANSITIONS[from].includes(to);
}

/** Every state a request in `from` may move to — for a UI or an error message. */
export function nextMaintenanceStatuses(
  from: MaintenanceStatus,
): readonly MaintenanceStatus[] {
  return MAINTENANCE_TRANSITIONS[from];
}

/**
 * `status_transitions.entity_type` for a maintenance request.
 *
 * One spelling, exported, so the writer and the test that reads the audit row
 * cannot disagree about it.
 */
export const MAINTENANCE_ENTITY_TYPE = "maintenance_request";

/**
 * `maintenance_events.kind` for the timeline row a status move writes.
 *
 * A second exported spelling for the same reason: the timeline and the tests that
 * read it must name the same value.
 */
export const MAINTENANCE_STATUS_EVENT_KIND = "status_transition" as const;

// ---------------------------------------------------------------------------
// The reopened window — §6.3's "(≤14 days)", as a pure predicate
// ---------------------------------------------------------------------------

/** The window §6.3 draws. Fourteen days, inclusive of the fourteenth. */
export const MAINTENANCE_REOPEN_WINDOW_DAYS = 14;

/** Milliseconds in one day. The window is a duration of instants, not of dates. */
const MS_PER_DAY = 86_400_000;

/** The facts the window predicate reads. Facts, not rows. */
export interface MaintenanceReopenWindowFacts {
  /** When the request reached `completed`, or NULL if it never did. */
  readonly completedAt: Date | null;
  /** The instant the reopen is being attempted. */
  readonly now: Date;
}

export interface MaintenanceReopenWindow {
  readonly allowed: boolean;
  readonly windowDays: number;
  /** The instant the window closes (`completedAt + 14 days`), when known. */
  readonly expiresAt: string | null;
  /** Whole milliseconds since completion, when known. Negative only on a clock skew. */
  readonly elapsedMs: number | null;
  readonly reason: string;
}

/**
 * **§6.3's reopened window, evaluated.**
 *
 * `completed → reopened` is legal while **at most 14 days** have elapsed since
 * the request was completed. The reading is deliberately INCLUSIVE at the
 * boundary, which is what "(≤14 days)" says: the window closes at the instant
 * `completedAt + 14 days` and reopens attempted at exactly that instant are still
 * allowed. Fourteen days and one millisecond is refused, and so is anything
 * later. `completedAt === null` is refused rather than assumed — a request with
 * no recorded completion instant has no window, and guessing one (from
 * `updated_at`, say) would reopen something nobody can date.
 *
 * The unit is a DURATION of instants, not a calendar day in some zone. §6.3 does
 * not say whose days, and a timezone-sensitive day count would make the answer
 * depend on a property's zone for a rule that is about elapsed time since a
 * recorded instant. If a zone-aware reading is ever wanted, that is a plan
 * change with a stated reason — not a quiet switch inside a route.
 */
export function evaluateMaintenanceReopenWindow(
  facts: MaintenanceReopenWindowFacts,
): MaintenanceReopenWindow {
  if (facts.completedAt === null) {
    return {
      allowed: false,
      windowDays: MAINTENANCE_REOPEN_WINDOW_DAYS,
      expiresAt: null,
      elapsedMs: null,
      reason:
        "this request has no recorded completion time, so there is no window to " +
        "reopen within — the move is refused rather than guessed",
    };
  }

  const windowMs = MAINTENANCE_REOPEN_WINDOW_DAYS * MS_PER_DAY;
  const elapsedMs = facts.now.getTime() - facts.completedAt.getTime();
  const expiresAt = new Date(facts.completedAt.getTime() + windowMs);

  if (elapsedMs > windowMs) {
    return {
      allowed: false,
      windowDays: MAINTENANCE_REOPEN_WINDOW_DAYS,
      expiresAt: expiresAt.toISOString(),
      elapsedMs,
      reason:
        `the reopen window is ${String(MAINTENANCE_REOPEN_WINDOW_DAYS)} days and it ` +
        `closed at ${expiresAt.toISOString()}`,
    };
  }

  return {
    allowed: true,
    windowDays: MAINTENANCE_REOPEN_WINDOW_DAYS,
    expiresAt: expiresAt.toISOString(),
    elapsedMs,
    reason:
      `the request was completed within the last ` +
      `${String(MAINTENANCE_REOPEN_WINDOW_DAYS)} days, so it may be reopened (§6.3)`,
  };
}

// ---------------------------------------------------------------------------
// Input schemas
// ---------------------------------------------------------------------------

/** Required, trimmed, length-bounded text. */
function requiredText(max: number) {
  return z.string().trim().min(1, "is required").max(max);
}

/**
 * Optional text stored as `null` when absent or blank — the same normalisation
 * `packages/shared/src/properties.ts` uses, and for the same reason: a cleared
 * form field must not persist as `""`, or "no description" gets two
 * representations and every later query has to remember both.
 */
function optionalText(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    );
}

/**
 * An optional id, blank → `null`.
 *
 * The blank case is allowed BEFORE the uuid check, not after: `.uuid()` first
 * would reject `""` with "must be a lease id", so a caller clearing the link
 * would be told they typed something wrong. Same trick, same reason, as
 * `leases.ts`'s optional dates.
 */
function optionalUuid(message: string) {
  const uuid = z.string().uuid(message);
  return z
    .string()
    .trim()
    .refine((value) => value === "" || uuid.safeParse(value).success, message)
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    );
}

/**
 * POST /workspaces/:workspaceId/maintenance body — §6.3's on-behalf logging.
 *
 * `channel` is one of the three on-behalf instruments (see
 * `MAINTENANCE_ON_BEHALF_CHANNELS`), and it is what makes this a landlord's
 * record of what a tenant reported rather than a portal submission. `status` is
 * NOT accepted: the §6.3 machine owns it, and a create body that could set it
 * would skip the transition route and its audit rows. `completedAt` is not
 * accepted either — it is the machine's own clock.
 *
 * `leaseId` is optional context (§5 makes it nullable); when it is sent the route
 * verifies it is a lease of the named unit.
 */
export const maintenanceRequestCreateSchema = z.object({
  unitId: z.string().uuid("must be a unit id"),
  leaseId: optionalUuid("must be a lease id"),
  /** Required — a request with no title is unusable in the list that finds it. */
  title: requiredText(200),
  description: optionalText(4000),
  /** §5/§6.3: the activation instrument. No `portal` on this surface. */
  channel: z.enum(MAINTENANCE_ON_BEHALF_CHANNEL_VALUES),
  /** A claim the caller makes; absent means "not an emergency". */
  emergency: z.boolean().default(false),
});

/**
 * POST /workspaces/:workspaceId/maintenance/:requestId/events body.
 *
 * A note, and only a note. `kind` is not accepted: the transition route writes the
 * `status_transition` rows, and a body that could set the kind would let a caller
 * write a state move the §6.3 machine never made (the database's shape CHECK
 * refuses the half-formed version of exactly that).
 *
 * **`photoFileIds` is accepted now, and it was not before (T0.8 Phase B).** This
 * schema used to refuse it on the grounds that "no upload route and no file UI exist
 * in this repo, so accepting ids here would let a caller assert a photo nobody can
 * open". That reasoning was correct while it held and is now false: `POST
 * /workspaces/:id/documents` stores a file and this API can serve it back, and
 * `AttachmentField` is the surface that picks one. The ids are therefore accepted on
 * the only row that can carry them — `maintenance_events.photo_file_ids` (0009) —
 * and the ROUTE verifies every id against `documents` in this workspace with the
 * `maintenance` domain before writing (see `apps/api/src/domain/documents.ts`).
 * Zod cannot do that check: it is a question about rows, not about a string.
 *
 * The array's CAP is a shape guard, not a policy about how many photos a repair may
 * have: a body carrying hundreds of ids is a client bug or an attack on the insert,
 * and refusing it at the boundary is cheaper than discovering it in the transaction.
 */
export const maintenanceEventCreateSchema = z.object({
  note: requiredText(2000),
  /** Photo document ids, each verified against `documents` before the row is written. */
  photoFileIds: z
    .array(z.string().uuid("must be a document id"))
    .max(20, "that is a lot of photos for one note")
    .default([]),
});

/** POST /workspaces/:workspaceId/maintenance/:requestId/transitions body. */
export const maintenanceTransitionSchema = z.object({
  to: z.enum(MAINTENANCE_STATUS_VALUES),
});

export type MaintenanceRequestCreateInput = z.infer<typeof maintenanceRequestCreateSchema>;
export type MaintenanceEventCreateInput = z.infer<typeof maintenanceEventCreateSchema>;
export type MaintenanceTransitionInput = z.infer<typeof maintenanceTransitionSchema>;

/** The PRE-parse shapes, for the SPA to build request bodies from. */
export type MaintenanceRequestCreateBody = z.input<typeof maintenanceRequestCreateSchema>;
export type MaintenanceEventCreateBody = z.input<typeof maintenanceEventCreateSchema>;
export type MaintenanceTransitionBody = z.input<typeof maintenanceTransitionSchema>;

// ---------------------------------------------------------------------------
// DTOs — what the API returns
// ---------------------------------------------------------------------------

/**
 * One maintenance request, as the API returns it.
 *
 * Declared here rather than inferred from the drizzle schema so the kernel does
 * not depend on a database package (dependency direction: apps → packages).
 * `completedAt` is an instant (ISO string), unlike the `YYYY-MM-DD` calendar
 * dates of the rent module: it is the reopened window's clock, and a calendar date
 * could not express a 14-day boundary that is inclusive at an instant.
 */
export interface MaintenanceRequestDto {
  readonly id: string;
  readonly unitId: string;
  /** Nullable per §5 — a make-ready repair on a vacant unit has no lease. */
  readonly leaseId: string | null;
  readonly status: MaintenanceStatus;
  readonly title: string;
  readonly description: string | null;
  /**
   * The activation instrument. `portal` can appear here (rows the portal will
   * write, T1.7); the landlord-side create route only ever writes the other
   * three, which is what `isOnBehalfChannel` reads.
   */
  readonly channel: MaintenanceChannel;
  readonly emergency: boolean;
  readonly completedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * One row of a request's timeline.
 *
 * `photoFileIds` is `null` for every row written before T0.8 Phase B and a
 * non-empty array for a note that attached photos — never `[]`, because "no photos"
 * has one representation (`null`) and an empty array would be the same fact spelled
 * twice. The ids are `documents` rows of the `maintenance` domain, verified by the
 * events route before the write; the reader fetches each one through `GET
 * /workspaces/:id/documents/:documentId`, which is what writes the `audit_log` row.
 */
export interface MaintenanceEventDto {
  readonly id: string;
  readonly maintenanceRequestId: string;
  readonly kind: MaintenanceEventKind;
  readonly note: string | null;
  readonly fromStatus: MaintenanceStatus | null;
  readonly toStatus: MaintenanceStatus | null;
  readonly photoFileIds: readonly string[] | null;
  readonly createdAt: string;
  /** The user who wrote it. NULL for a row no person wrote. */
  readonly createdBy: string | null;
}

/**
 * A request plus the context that makes it identifiable: the unit, the property
 * and the tenancy.
 *
 * The Maintenance screen answers "what needs fixing, and where", so each row
 * names its building without a request per row — the same reasoning as
 * `LeaseListRowDto`. `leaseStatus` is nullable because the LINK is: a request on a
 * vacant unit has no tenancy, and that is a real state, not a missing join.
 */
export interface MaintenanceListRowDto extends MaintenanceRequestDto {
  readonly leaseStatus: LeaseStatus | null;
  readonly unitLabel: string;
  readonly propertyId: string;
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
}

/**
 * GET /workspaces/:workspaceId/maintenance — every request in the portfolio.
 *
 * `counts` and `byStatus` are folded from the rows returned beside them, so every
 * figure can be checked against the list. There is deliberately **no cost, no
 * response time and no SLA**: nothing in this build measures any of them, and a
 * rendered "0 h average response" would be a claim about the world rather than
 * about the rows.
 */
export interface MaintenanceListDto {
  /** True when the list was cut at the server cap: these are the first rows, not all of them. */
  readonly truncated?: true;
  readonly counts: {
    readonly requests: number;
    /** Distinct units with at least one request — not the count of requests. */
    readonly units: number;
    /** Requests the caller flagged as an emergency. A claim the row carries. */
    readonly emergency: number;
  };
  /** Only the statuses that actually occur, in §6.3 order. */
  readonly byStatus: readonly {
    readonly status: MaintenanceStatus;
    readonly requests: number;
  }[];
  readonly requests: readonly MaintenanceListRowDto[];
}

/**
 * GET /workspaces/:workspaceId/maintenance/:requestId — one request and its
 * timeline, oldest first (a timeline reads forward; the list reads newest first
 * because the request just logged is the one being looked for).
 */
export interface MaintenanceRequestDetailDto extends MaintenanceListRowDto {
  readonly events: readonly MaintenanceEventDto[];
}
